import React from "react";
import { fmtMoney, fmtNumber } from "../utils/amortizationHelpers";

/**
 * ***************************************************************
 * ADJUSTABLE AMORTIZATION TABLE
 * ***************************************************************
 *
 * Allows editing of:
 *   - Days
 *   - Payment
 *
 * Props:
 *   rows        -> array of amortization row objects
 *   onEditDays  -> function to update the days value for a row
 *  onEditPayment -> function to update the payment amount for a row
 *
 * Each row contains:
 *   paymentNumber
 *   daysInPeriod
 *   beginningBalance
 *   paymentAmount
 *   interestPaid
 *   principalPaid
 *   endingBalance
 *   cumulativeInterest
 *   cumulativePrincipal
 *
 * ***************************************************************
 */

function AdjustableAmortizationTable({ 
    rows = [], 
    onEditDays,
    onEditPayment
 }) {
  return (
    <div className="amortization-table-wrap">
      <table className="amortization-table">
        
        {/* ================= TABLE HEADER ================= */}
        <thead>
          <tr>
            <th>#</th>
            <th>Days</th>
            <th>Beginning<br />Balance</th>
            <th>Payment</th>
            <th>Interest</th>
            <th>Principal</th>
            <th>Ending<br />Balance</th>
            <th>Cumulative<br />Interest</th>
            <th>Cumulative<br />Principal</th>
          </tr>
        </thead>

        {/* ================= TABLE BODY ================= */}
        <tbody>
          {/* If no rows exist, show a placeholder message */}
          {rows.length === 0 ? (
            <tr>
              <td colSpan="9" className="amortization-empty">
                No amortization schedule to display.
              </td>
            </tr>
          ) : (
            /* Map each row of amortization data to a table row */
            rows.map((row, index) => (
              <tr key={row.paymentNumber}>
                
                {/* Payment number */}
                <td>{fmtNumber(row.paymentNumber)}</td>

                {/* =====================================================
                   EDITABLE DAYS INPUT
                   -----------------------------------------------------
                   - Allows user to change number of days in period
                   - Calls onEditDays when value changes
                   - index identifies which row is being edited
                   ===================================================== */}
                <td>
                  <input
                    id = {`days-input-${index}`}
                    type="number"
                    min="1"
                    value={row.daysInPeriod}
                    onChange={(e) =>
                      onEditDays(index, e.target.value)
                    }
                    className="amortization-days-input"
                  />
                </td>

                {/* Beginning balance */}
                <td>{"$" + fmtMoney(row.beginningBalance)}</td>

                {/* Payment amount */}
                <td>
                  <input
                    id = {`payment-${index}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={Number(row.paymentAmount).toFixed(2)}
                    onChange={(e) => onEditPayment(index, e.target.value)}
                    className="amortization-payment-input"
                  />
                </td>

                {/* Interest paid for this period */}
                <td>{"$" + fmtMoney(row.interestPaid)}</td>

                {/* Principal paid for this period */}
                <td>{"$" + fmtMoney(row.principalPaid)}</td>

                {/* Remaining balance after payment */}
                <td>{"$" + fmtMoney(row.endingBalance)}</td>

                {/* Running total of interest */}
                <td>{"$" + fmtMoney(row.cumulativeInterest)}</td>

                {/* Running total of principal */}
                <td>{"$" + fmtMoney(row.cumulativePrincipal)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AdjustableAmortizationTable;